import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, BarChart3, Calendar, Target, 
  ArrowUp, ArrowDown, Clock, DollarSign,
  Package, Users, Building2, AlertTriangle,
  CheckCircle2, XCircle, RefreshCw, Settings,
  Download, Eye, Edit, Plus, Filter
} from 'lucide-react';

const PlanningForecastingTab = ({ timeRange, region }) => {
  const [activeSubTab, setActiveSubTab] = useState('forecasting');
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const subTabs = [
    { id: 'forecasting', label: 'Demand Forecasting', icon: TrendingUp, description: 'Demand forecasting and analysis' },
    { id: 'planning', label: 'Procurement Planning', icon: Target, description: 'Procurement and stock level planning' },
    { id: 'analysis', label: 'Lead Time Analysis', icon: Clock, description: 'Lead time and supplier analysis' },
    { id: 'trends', label: 'Seasonal Trends', icon: BarChart3, description: 'Seasonal patterns and trends' }
  ];

  // Mock data for demand forecasting
  const demandForecast = [
    {
      item: 'Kitchen Knife Set',
      currentDemand: 125,
      forecastedDemand: 145,
      trend: 'up',
      confidence: 85,
      category: 'Kitchen Equipment'
    },
    {
      item: 'Commercial Oven',
      currentDemand: 8,
      forecastedDemand: 12,
      trend: 'up',
      confidence: 78,
      category: 'Kitchen Equipment'
    },
    {
      item: 'Refrigeration Unit',
      currentDemand: 5,
      forecastedDemand: 3,
      trend: 'down',
      confidence: 92,
      category: 'Kitchen Equipment'
    }
  ];

  // Mock data for procurement planning
  const procurementPlan = [
    {
      item: 'Kitchen Knife Set',
      currentStock: 125,
      minStock: 50,
      recommendedOrder: 75,
      supplier: 'Al-Rashid Trading Co.',
      leadTime: 7,
      orderDate: '2024-03-20',
      estimatedCost: 33750
    },
    {
      item: 'Commercial Oven',
      currentStock: 8,
      minStock: 10,
      recommendedOrder: 15,
      supplier: 'Kitchen Pro Supplies',
      leadTime: 14,
      orderDate: '2024-03-15',
      estimatedCost: 127500
    }
  ];

  // Mock data for lead time analysis
  const leadTimeAnalysis = [
    {
      supplier: 'Al-Rashid Trading Co.',
      avgLeadTime: 7,
      onTimeDelivery: 95,
      category: 'Kitchen Equipment',
      totalOrders: 156,
      performance: 'excellent'
    },
    {
      supplier: 'Tech Solutions Ltd.',
      avgLeadTime: 12,
      onTimeDelivery: 88,
      category: 'IT Equipment',
      totalOrders: 89,
      performance: 'good'
    },
    {
      supplier: 'Office Supplies Pro',
      avgLeadTime: 5,
      onTimeDelivery: 92,
      category: 'Office Supplies',
      totalOrders: 234,
      performance: 'excellent'
    }
  ];

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-green-600' : 'text-red-600';
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const getPerformanceColor = (performance) => {
    const colors = {
      excellent: 'bg-green-100 text-green-800',
      good: 'bg-blue-100 text-blue-800',
      fair: 'bg-yellow-100 text-yellow-800',
      poor: 'bg-red-100 text-red-800'
    };
    return colors[performance] || colors.fair;
  };

  const renderForecasting = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Demand Forecasting</h3>
          <p className="text-sm text-gray-500 mt-1">Predict future demand based on historical data and trends</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="week">Next Week</option>
            <option value="month">Next Month</option>
            <option value="quarter">Next Quarter</option>
            <option value="year">Next Year</option>
          </select>
          <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
            <RefreshCw className="w-4 h-4" />
            Update Forecast
          </button>
        </div>
      </div>

      {/* Forecast Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Items', value: demandForecast.length, color: 'blue' },
          { label: 'Avg Confidence', value: '85%', color: 'green' },
          { label: 'Trending Up', value: demandForecast.filter(d => d.trend === 'up').length, color: 'green' },
          { label: 'Trending Down', value: demandForecast.filter(d => d.trend === 'down').length, color: 'red' }
        ].map((stat, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                <TrendingUp className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Forecast Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Demand
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Forecasted Demand
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Confidence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {demandForecast.map((item) => (
                <tr key={item.item} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.item}</p>
                      <p className="text-xs text-gray-500">{item.category}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">{item.currentDemand}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">{item.forecastedDemand}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {getTrendIcon(item.trend)}
                      <span className={`ml-1 text-sm font-medium ${getTrendColor(item.trend)}`}>
                        {item.trend === 'up' ? 'Increasing' : 'Decreasing'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full" 
                          style={{ width: `${item.confidence}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-900">{item.confidence}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderPlanning = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Procurement Planning</h3>
          <p className="text-sm text-gray-500 mt-1">Stock level planning and recommended orders</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          Generate Plan
        </button>
      </div>

      {/* Planning Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Items to Order', value: procurementPlan.length, color: 'blue' },
          { label: 'Total Value', value: `SAR ${procurementPlan.reduce((sum, item) => sum + item.estimatedCost, 0).toLocaleString()}`, color: 'green' },
          { label: 'Avg Lead Time', value: `${Math.round(procurementPlan.reduce((sum, item) => sum + item.leadTime, 0) / procurementPlan.length)} days`, color: 'orange' },
          { label: 'Suppliers', value: new Set(procurementPlan.map(item => item.supplier)).size, color: 'purple' }
        ].map((stat, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                <Target className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Planning Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recommended Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estimated Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {procurementPlan.map((item) => (
                <tr key={item.item} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">{item.item}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{item.currentStock}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-emerald-600">{item.recommendedOrder}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{item.supplier}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-900">{item.leadTime} days</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      SAR {item.estimatedCost.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <Plus className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderLeadTimeAnalysis = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Lead Time Analysis</h3>
          <p className="text-sm text-gray-500 mt-1">Supplier performance and lead time optimization</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Download className="w-4 h-4" />
          Export Report
        </button>
      </div>

      {/* Lead Time Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Avg Lead Time', value: `${Math.round(leadTimeAnalysis.reduce((sum, s) => sum + s.avgLeadTime, 0) / leadTimeAnalysis.length)} days`, color: 'blue' },
          { label: 'On-time Delivery', value: `${Math.round(leadTimeAnalysis.reduce((sum, s) => sum + s.onTimeDelivery, 0) / leadTimeAnalysis.length)}%`, color: 'green' },
          { label: 'Total Suppliers', value: leadTimeAnalysis.length, color: 'purple' },
          { label: 'Total Orders', value: leadTimeAnalysis.reduce((sum, s) => sum + s.totalOrders, 0), color: 'orange' }
        ].map((stat, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                <Clock className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Lead Time Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Lead Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  On-time Delivery
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leadTimeAnalysis.map((supplier) => (
                <tr key={supplier.supplier} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">{supplier.supplier}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{supplier.category}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-900">{supplier.avgLeadTime} days</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full" 
                          style={{ width: `${supplier.onTimeDelivery}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-900">{supplier.onTimeDelivery}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{supplier.totalOrders}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPerformanceColor(supplier.performance)}`}>
                      {supplier.performance}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTrends = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Seasonal Trends</h3>
          <p className="text-sm text-gray-500 mt-1">Seasonal patterns and demand trends analysis</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <BarChart3 className="w-4 h-4" />
          Generate Report
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-12">
          <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Seasonal Trends Analysis</h3>
          <p className="text-gray-500 mb-6">View detailed seasonal patterns and demand trends</p>
          <div className="flex justify-center space-x-4">
            <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
              View Trends
            </button>
            <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
              Download Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex space-x-1">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSubTab === tab.id
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={tab.description}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content based on active sub-tab */}
      {activeSubTab === 'forecasting' && renderForecasting()}
      {activeSubTab === 'planning' && renderPlanning()}
      {activeSubTab === 'analysis' && renderLeadTimeAnalysis()}
      {activeSubTab === 'trends' && renderTrends()}
    </div>
  );
};

export default PlanningForecastingTab; 